import React from 'react';
import { Helmet } from 'react-helmet';
import {
  TrendingUpIcon,
  LightBulbIcon,
  PaperAirplaneIcon,
  LightningBoltIcon,
  EmojiHappyIcon,
  BadgeCheckIcon,
} from '@heroicons/react/outline';
import Layout from '../components/Layout';
import { FeaturePositions } from '../components/Feature';
import FeatureSection from '../components/FeatureSection';
import GetReadySection from '../components/GetReadySection';
import HeroSection from '../components/HeroSection';
import MetricsSection from '../components/MetricsSection';
import Taichi100M from '../videos/branding/Taichi100M.mp4';
import LifeOfTaichiKernel from '../images/branding/life_of_kernel.png';
import TaichiRepo from '../images/branding/taichi_repo.png';
import AnimatedTaichiFractal from '../components/AnimatedTaichiFractal';
import AnimatedTaichiPortability from '../components/AnimatedTaichiPortability';

// FeatureSection data
const features = [
  {
    title: 'Productivity',
    description: 'Embrace the simplicity from Python.',
    panel: <AnimatedTaichiFractal language="python" title="fractal.py" />,
    position: FeaturePositions.Right,
    features: [
      {
        name: 'Friendly Learning Curve',
        description: 'As a language embedded in Python, Taichi has a Python-style syntax which is extremely easy to learn.',
        icon: TrendingUpIcon,
      },
      {
        name: 'Less Code with Higher Performance',
        description: 'Research shows Taichi programs are 10x shorter compared to equivalent C++/CUDA code while achieving higher performance.',
        icon: LightBulbIcon,
      },
    ],
  },
  {
    title: 'Portability',
    description: 'Write once, run anywhere.',
    panel: <AnimatedTaichiPortability />,
    position: FeaturePositions.Left,
    features: [
      {
        name: 'Multi-backend Support',
        description: 'Taichi\'s multiple CPU/GPU backends empowers your source code to be written once and executed everywhere. Currently, Taichi supports a wide range of backends, including x64 & ARM CPUs, CUDA, OpenGL Compute Shader and Apple Metal, with more GPU backends coming along. Made transparent to the various GPU APIs, your code gains the optimal performance on all the major OS platforms for free.',
        icon: PaperAirplaneIcon,
      },
    ],
  },
  {
    title: 'Performance',
    description: '',
    panel: <img
      className="relative mx-auto transform scale-110 rounded-md shadow-2xl"
      width="490"
      src={LifeOfTaichiKernel}
      alt=""
    />,
    position: FeaturePositions.Right,
    features: [
      {
        name: 'Blazing Fast',
        description: 'Taichi program is JIT compiled and runs natively on the massively parallel GPU or multi-core CPU backend. The language provides a rich set of features, including high-performance spatial sparse data structures, autodiff and quantization, which are optimized for bandwidth, locality and load balancing.',
        icon: LightningBoltIcon,
      },
    ],
  },
  {
    title: 'Open Source',
    description: 'Taichi fosters the open source community.',
    panel: <img
      className="relative mx-auto transform scale-110 rounded-md shadow-2xl"
      width="490"
      src={TaichiRepo}
      alt=""
    />,
    position: FeaturePositions.Left,
    features: [
      {
        name: 'Trust and Transparency',
        description: 'We are trusted by the open source community and would love to give back to the commnuity. Taichi language is MIT licensed, no lock-in.',
        icon: BadgeCheckIcon,
      },
      {
        name: 'Collaboration',
        description: 'Contributors, developers and users from all over the world are supporting us and giving us feedback.',
        icon: EmojiHappyIcon,
      },
    ],
  },
];

// MetricsSection data
// TODO: pull the metrics data in real-time from API
const metrics = [
  {
    id: 1,
    stat: '14.8k+',
    emphasis: 'Stars',
    rest: 'on Github.',
  },
  {
    id: 2,
    stat: '500k+',
    emphasis: 'Total Downloads',
    rest: 'from PyPI.',
  },
  {
    id: 3,
    stat: '60+',
    emphasis: 'Active Contributors',
    rest: 'from the community.',
  },
  {
    id: 4,
    stat: '10+',
    emphasis: 'Partners and Customers',
    rest: 'from academia and industry.',
  },
];

/**
 * The index page.
 */
export default function IndexPage() {
  return (
    <Layout>
      <Helmet>
        <title>Taichi Graphics</title>
        <meta name="index" content="Taichi Graphics Index Page" />
      </Helmet>
      <HeroSection
        backgroundAsset={(
          <video
            className="absolute inset-0 w-full h-full object-cover"
            playsInline
            autoPlay
            loop
            muted
            src={Taichi100M}
          />
        )}
        description="Taichi Graphics"
      />
      <FeatureSection
        title="Why Taichi?"
        description=""
        features={features}
      />
      <MetricsSection
        image=""
        imageAlt=""
        title="Adopted and Trusted"
        subtitle="Get started in Graphics with Taichi"
        description="We are trusted by the open source community and leaders from academia and industry"
        metrics={metrics}
      />
      <GetReadySection
        title="Ready to dive in Taichi?"
        subtitle="Install and write your first Taichi program!"
      />
    </Layout>
  );
}
